import {
  DocumentData,
  Query,
  count,
  getAggregateFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  sum,
  where
} from "firebase/firestore";
import { COLLECTIONS } from "./collectionNames";
import { adminCollection, dateField, logDevelopmentError, stringArrayField, stringField } from "./firestoreHelpers";

export type FounderStoryEventKind =
  | "joined"
  | "service"
  | "event"
  | "challenge"
  | "booking"
  | "revenue"
  | "milestone";

export type FounderStoryEvent = {
  id: string;
  kind: FounderStoryEventKind;
  icon: string;
  title: string;
  detail: string;
  source: string;
  occurredAt: Date;
};

export type FounderStoryTimeline = {
  events: FounderStoryEvent[];
  lastUpdated: Date;
};

const LIVE_LIMIT = 24;
const PAID_REVENUE_STATUS = "paid";

function displayName(data: DocumentData, fallback: string) {
  return stringField(data, "displayName") ?? stringField(data, "fullName") ?? stringField(data, "username") ?? fallback;
}

function titleField(data: DocumentData, fallback: string) {
  return stringField(data, "title") ?? stringField(data, "name") ?? stringField(data, "serviceTitle") ?? fallback;
}

function formatCount(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatUSD(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value);
}

function rolesFor(data: DocumentData) {
  const roles = new Set<string>();
  const scalarFields = ["role", "activeRole", "primaryRole", "verificationType", "accountType"];
  const arrayFields = ["roles", "assignedRoles", "completedRoleTargets"];

  scalarFields.forEach((field) => {
    const value = stringField(data, field);
    if (value) roles.add(value.toLowerCase());
  });

  arrayFields.forEach((field) => {
    stringArrayField(data, field).forEach((value) => roles.add(value.toLowerCase()));
  });

  if (data.isCoach === true) roles.add("coach");
  return roles;
}

function locationCountry(data: DocumentData) {
  const location = data.location;
  if (location && typeof location === "object" && !Array.isArray(location)) {
    const country = (location as Record<string, unknown>).country;
    if (typeof country === "string") return country;
  }

  return stringField(data, "country") ?? stringField(data, "locationCountry");
}

function isInternationalCoach(data: DocumentData) {
  const roles = rolesFor(data);
  const country = locationCountry(data)?.toLowerCase();
  return roles.has("coach") && country !== undefined && !["united states", "usa", "us"].includes(country);
}

function eventDate(data: DocumentData) {
  return dateField(data, "createdAt") ?? dateField(data, "completedAt") ?? dateField(data, "updatedAt") ?? new Date();
}

function userStory(id: string, data: DocumentData): FounderStoryEvent {
  const name = displayName(data, "Someone");
  const internationalCoach = isInternationalCoach(data);
  return {
    id: `user-${id}`,
    kind: "joined",
    icon: internationalCoach ? "🌍" : "🚀",
    title: internationalCoach ? "International coach joined." : `${name} joined.`,
    detail: internationalCoach ? `${name} joined Chor from ${locationCountry(data)}.` : "A new member entered the Chor community.",
    source: COLLECTIONS.users,
    occurredAt: eventDate(data)
  };
}

function serviceStory(id: string, data: DocumentData): FounderStoryEvent {
  const creator = stringField(data, "creatorDisplayName") ?? stringField(data, "hostDisplayName") ?? "A coach";
  const title = titleField(data, "a service");
  return {
    id: `service-${id}`,
    kind: "service",
    icon: "✨",
    title: `${creator} published a service.`,
    detail: title,
    source: COLLECTIONS.classSessions,
    occurredAt: eventDate(data)
  };
}

function eventStory(id: string, data: DocumentData): FounderStoryEvent {
  const creator = stringField(data, "hostDisplayName") ?? stringField(data, "creatorDisplayName") ?? stringField(data, "organizationName") ?? "A creator";
  return {
    id: `event-${id}`,
    kind: "event",
    icon: "🎟",
    title: `${creator} created an event.`,
    detail: titleField(data, "New event"),
    source: COLLECTIONS.events,
    occurredAt: eventDate(data)
  };
}

function challengeSubmissionStory(id: string, data: DocumentData): FounderStoryEvent {
  const name = stringField(data, "displayName") ?? stringField(data, "submitterDisplayName") ?? "A dancer";
  return {
    id: `submission-${id}`,
    kind: "challenge",
    icon: "🏆",
    title: `${name} submitted a challenge.`,
    detail: stringField(data, "challengeTitle") ?? "Challenge submission activity.",
    source: COLLECTIONS.challengeSubmissions,
    occurredAt: eventDate(data)
  };
}

function bookingStory(id: string, data: DocumentData): FounderStoryEvent {
  return {
    id: `booking-${id}`,
    kind: "booking",
    icon: "✅",
    title: stringField(data, "status") === "completed" ? "Booking completed." : "Booking created.",
    detail: titleField(data, "Service booking"),
    source: COLLECTIONS.bookings,
    occurredAt: eventDate(data)
  };
}

function revenueStory(id: string, data: DocumentData): FounderStoryEvent {
  const amount = typeof data.platformFeeUSD === "number" ? formatUSD(data.platformFeeUSD) : "Revenue";
  return {
    id: `revenue-${id}`,
    kind: "revenue",
    icon: "💰",
    title: "Revenue milestone moved.",
    detail: `${amount} platform revenue from ${stringField(data, "sourceType") ?? "marketplace activity"}.`,
    source: COLLECTIONS.marketplaceTransactions,
    occurredAt: eventDate(data)
  };
}

async function countAll(collectionName: string) {
  const snapshot = await getAggregateFromServer(query(adminCollection(collectionName)), { total: count() });
  return snapshot.data().total ?? 0;
}

async function revenueTotal() {
  const snapshot = await getAggregateFromServer(
    query(adminCollection(COLLECTIONS.marketplaceTransactions), where("status", "==", PAID_REVENUE_STATUS)),
    { total: sum("platformFeeUSD") }
  );
  return snapshot.data().total ?? 0;
}

async function milestoneStories(): Promise<FounderStoryEvent[]> {
  try {
    const [submissions, bookings, revenue] = await Promise.all([
      countAll(COLLECTIONS.challengeSubmissions),
      countAll(COLLECTIONS.bookings),
      revenueTotal()
    ]);
    const now = new Date();
    const milestones: FounderStoryEvent[] = [];

    if (submissions > 0) {
      milestones.push({
        id: "milestone-challenge-submissions",
        kind: "milestone",
        icon: "🏆",
        title: `${formatCount(submissions)} dancers submitted challenges.`,
        detail: "Challenge participation is becoming part of Chor's rhythm.",
        source: COLLECTIONS.challengeSubmissions,
        occurredAt: now
      });
    }

    if (bookings >= 1000) {
      milestones.push({
        id: "milestone-bookings-1000",
        kind: "milestone",
        icon: "🎉",
        title: "1000th booking completed.",
        detail: `${formatCount(bookings)} bookings have moved through Chor.`,
        source: COLLECTIONS.bookings,
        occurredAt: now
      });
    } else if (bookings > 0) {
      milestones.push({
        id: "milestone-bookings",
        kind: "milestone",
        icon: "✅",
        title: `${formatCount(bookings)} bookings created.`,
        detail: "The marketplace story is taking shape.",
        source: COLLECTIONS.bookings,
        occurredAt: now
      });
    }

    if (revenue > 0) {
      milestones.push({
        id: "milestone-revenue",
        kind: "milestone",
        icon: "💰",
        title: "Revenue milestone reached.",
        detail: `${formatUSD(revenue)} platform revenue recorded.`,
        source: COLLECTIONS.marketplaceTransactions,
        occurredAt: now
      });
    }

    return milestones;
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

function mergeStories(storyGroups: Map<string, FounderStoryEvent[]>, milestones: FounderStoryEvent[]) {
  return [...milestones, ...Array.from(storyGroups.values()).flat()]
    .sort((left, right) => right.occurredAt.getTime() - left.occurredAt.getTime())
    .slice(0, 40);
}

export function subscribeFounderStoryTimeline(
  onUpdate: (timeline: FounderStoryTimeline) => void,
  onError: (error: Error) => void
) {
  const storyGroups = new Map<string, FounderStoryEvent[]>();
  let milestones: FounderStoryEvent[] = [];

  function emit() {
    onUpdate({
      events: mergeStories(storyGroups, milestones),
      lastUpdated: new Date()
    });
  }

  function listen(collectionName: string, queryRef: Query<DocumentData>, mapper: (id: string, data: DocumentData) => FounderStoryEvent) {
    return onSnapshot(
      queryRef,
      (snapshot) => {
        storyGroups.set(
          collectionName,
          snapshot.docs.map((document) => mapper(document.id, document.data()))
        );
        void milestoneStories().then((nextMilestones) => {
          milestones = nextMilestones;
          emit();
        });
      },
      (error) => {
        logDevelopmentError(error);
        onError(error);
      }
    );
  }

  const unsubscribers = [
    listen(COLLECTIONS.users, query(adminCollection(COLLECTIONS.users), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), userStory),
    listen(COLLECTIONS.classSessions, query(adminCollection(COLLECTIONS.classSessions), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), serviceStory),
    listen(COLLECTIONS.events, query(adminCollection(COLLECTIONS.events), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), eventStory),
    listen(COLLECTIONS.challengeSubmissions, query(adminCollection(COLLECTIONS.challengeSubmissions), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), challengeSubmissionStory),
    listen(COLLECTIONS.bookings, query(adminCollection(COLLECTIONS.bookings), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), bookingStory),
    listen(COLLECTIONS.marketplaceTransactions, query(adminCollection(COLLECTIONS.marketplaceTransactions), orderBy("createdAt", "desc"), limit(LIVE_LIMIT)), revenueStory)
  ];

  void milestoneStories().then((nextMilestones) => {
    milestones = nextMilestones;
    emit();
  });

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}
